// src/oura/components/onboarding/EmailsCard.tsx
//
// The onboarding Emails card: every email sent to the client, and whether the
// client has done what it asked. Built only from what the onboarding payload
// already carries (the sent list, the form, the login and the access list).

import { FileTextIcon, KeyRoundIcon, LogInIcon, MailIcon, SendIcon, type LucideIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { FoldCard, IconSquare, ListRow, ShowMore, Tag, TYPE, type Tone } from "../../ui";
import type { Onboarding, SentMessage } from "@service/aeo/onboarding";
import { REMIND_AFTER_DAYS, accessAsked, accessRowOf, beforeFlowSwitch, callName, dayMonth, daysSince, isHeld, isOneSetup } from "../../onboarding/derive";
import { MESSAGE_LABEL } from "../../onboarding/drafts";

/** What an email asked the client to do. A reminder asks the same as the email it follows. */
type Ask = "form" | "login" | "access";
const ASK_OF: Record<SentMessage["kind"], Ask> = {
    kickoff: "form",
    form_reminder: "form",
    login_invite: "login",
    // The one-off tour carries the login link too (backend #6473).
    account_ready: "login",
    access_request: "access",
    access_reminder: "access",
};
const ASK_ICON: Record<Ask, LucideIcon> = { form: FileTextIcon, login: LogInIcon, access: KeyRoundIcon };
/** How the summary line names each ask: "Form and login done. Waiting on Wei Jie for access." */
const ASK_DONE: Record<Ask, string> = { form: "Form", login: "Login", access: "Access" };
const ASK_ORDER: Ask[] = ["form", "login", "access"];
const ASK_WAIT: Record<Ask, string> = { form: "the form", login: "the first sign-in", access: "access" };

/**
 * One setup: the Setup email asks for the questions and the access together,
 * so its ask is the whole setup, and its reminder is a setup reminder.
 */
const askDone = (ask: Ask, o: Onboarding) => (ask === "form" && isOneSetup(o) ? "Setup" : ASK_DONE[ask]);
const askWait = (ask: Ask, o: Onboarding) => (ask === "form" && isOneSetup(o) ? "setup" : ASK_WAIT[ask]);
function titleOf(m: SentMessage, o: Onboarding): string {
    // An old kick-off from before the move onto one setup keeps its old name (#4454).
    if (!isOneSetup(o) || beforeFlowSwitch(o, m)) return MESSAGE_LABEL[m.kind];
    return m.kind === "kickoff" ? "Setup email" : m.kind === "form_reminder" ? "Setup reminder" : MESSAGE_LABEL[m.kind];
}

/** Shown on the newest email of each ask; older ones in the same chain carry none. */
interface Outcome { ask: Ask; label: string; tone: Tone; done: boolean; failed?: boolean; who: string }

const SHOWN = 3;

/**
 * One setup: when the client finished everything that is theirs to do (the
 * answers, and every access row given or said done), the latest of those
 * dates. Null while anything is still theirs.
 */
function clientPartDoneAt(o: Onboarding): string | null {
    if (!isOneSetup(o) || !o.form.answeredAt) return null;
    const asked = accessAsked(o);
    if (asked.some((a) => !isHeld(a) && a.status !== "claimed")) return null;
    const dates = [o.form.answeredAt, ...asked.map((a) => (a.status === "claimed" ? a.claimedAt : a.since))].filter((d): d is string => !!d);
    return dates.sort().at(-1) ?? o.form.answeredAt;
}

function outcomeOf(ask: Ask, latest: SentMessage, o: Onboarding, now: number): Outcome {
    const who = callName(latest.to[0]?.name ?? "the client");
    return { ask, who, ...stateOf(ask, latest, o, now) };
}

function stateOf(ask: Ask, latest: SentMessage, o: Onboarding, now: number): Pick<Outcome, "label" | "tone" | "done" | "failed"> {
    if (ask === "form" && isOneSetup(o)) {
        // Counted as the Steps list counts them: Search Console and Analytics are 1 row.
        const asked = accessAsked(o);
        const rows = Array.from(new Set(asked.map((a) => accessRowOf(a.key))));
        const total = 1 + rows.length;
        const held = (o.form.answeredAt ? 1 : 0) + rows.filter((r) => asked.filter((a) => accessRowOf(a.key) === r).every(isHeld)).length;
        if (held === total) return { label: "Setup done", tone: "green", done: true };
        // Every part the client can do is done: what is left (invites to confirm) is ours, not theirs.
        if (clientPartDoneAt(o)) return { label: "Their part done", tone: "green", done: true };
        if (held > 0) return { label: `${held} of ${total} done`, tone: "gray", done: false };
    } else if (ask === "form" && o.form.answeredAt) return { label: `Answered ${dayMonth(o.form.answeredAt)}`, tone: "green", done: true };
    if (ask === "login") {
        if (o.login.status === "signed_in") return { label: "Signed in", tone: "green", done: true };
        // On its way, a few seconds: in progress, not waiting on the client (ourteam-backend#5523).
        if (o.login.status === "sending") return { label: "Sending", tone: "blue", done: false };
        // The server's own word that nothing went: never read as waiting on the client.
        if (o.login.status === "send_failed") return { label: "Not sent", tone: "blue", done: false, failed: true };
    }
    if (ask === "access" && o.access.length > 0) {
        const held = o.access.filter(isHeld).length;
        if (held === o.access.length) return { label: "Access given", tone: "green", done: true };
        if (held > 0) return { label: `${held} of ${o.access.length} given`, tone: "gray", done: false };
    }
    const quiet = daysSince(latest.sentAt, now) ?? 0;
    if (quiet >= REMIND_AFTER_DAYS) return { label: `No reply in ${quiet} days`, tone: "gray", done: false };
    return { label: "Waiting", tone: "gray", done: false };
}

export function EmailsCard({ onboarding: o, onOpen }: { onboarding: Onboarding; onOpen: (m: SentMessage) => void }) {
    const now = Date.now();
    // Onboarding emails only. `sent` also carries the document emails (`doc_delivery`,
    // backend #6697), which have no ask here and show in the document panel; a kind this
    // card does not know drew no icon and took the whole tab down (#4856).
    const sent = o.sent
        .filter((m) => Object.prototype.hasOwnProperty.call(ASK_OF, m.kind))
        .sort((a, b) => b.sentAt.localeCompare(a.sentAt));

    // The newest email of each ask is the one that carries how it went.
    const outcomes = new Map<string, Outcome>();
    const seen = new Set<Ask>();
    for (const m of sent) {
        const ask = ASK_OF[m.kind];
        // Old-flow history from before the move onto one setup carries no outcome (#4454).
        if (seen.has(ask) || beforeFlowSwitch(o, m)) continue;
        seen.add(ask);
        outcomes.set(m.id, outcomeOf(ask, m, o, now));
    }
    // In the order the client meets them: form, login, then access.
    const all = Array.from(outcomes.values()).sort((a, b) => ASK_ORDER.indexOf(a.ask) - ASK_ORDER.indexOf(b.ask));
    const done = all.filter((x) => x.done).map((x, i) => (i === 0 ? askDone(x.ask, o) : askDone(x.ask, o).toLowerCase()));
    // One setup, client's part done: say so, and what is left for us (Jack, 20 Oct 2026).
    const partDone = clientPartDoneAt(o);
    // Invites only: Search Console and Analytics are connected, not confirmed (#4513).
    const toConfirm = accessAsked(o).filter((a) => a.status === "claimed" && (a.key === "gbp" || a.key === "website")).length;
    const partLine = partDone && !accessAsked(o).every(isHeld)
        ? `${callName(o.contact.name)}'s part is done, ${dayMonth(partDone)}.${toConfirm > 0 ? ` ${toConfirm === 1 ? "1 invite" : `${toConfirm} invites`} to confirm.` : ""}`
        : null;
    const summary = [
        partLine,
        !partLine && done.length > 0 && `${done.join(" and ").replace(/^(\w+) and (\w+) and/, "$1, $2 and")} done.`,
        ...all.filter((x) => x.failed).map((x) => `The ${askDone(x.ask, o).toLowerCase()} email to ${x.who} failed.`),
        ...all.filter((x) => !x.done && !x.failed).map((x) => `Waiting on ${x.who} for ${askWait(x.ask, o)}.`),
    ].filter(Boolean).join(" ");

    // The results and plan go in one email from the card's Send (One Setup): its own row, newest first.
    const resultsRow = o.resultsSentAt ? (
        <ListRow
            key="results"
            lead="square"
            icon={<IconSquare tone="gray" icon={SendIcon} />}
            title="Results and plan"
            sub={`${dayMonth(o.resultsSentAt)} · To ${callName(o.contact.name)}`}
            tags={<Tag tone="green">Sent</Tag>}
        />
    ) : null;
    const count = sent.length + (resultsRow ? 1 : 0);

    const row = (m: SentMessage) => {
        const outcome = outcomes.get(m.id);
        const cc = m.cc.length;
        return (
            <ListRow
                key={m.id}
                lead="square"
                icon={<IconSquare tone="gray" icon={ASK_ICON[ASK_OF[m.kind]]} />}
                title={titleOf(m, o)}
                sub={[
                    dayMonth(m.sentAt),
                    `To ${m.to.map((p) => callName(p.name)).join(", ")}`,
                    cc > 0 && `cc ${cc}`,
                ].filter(Boolean).join(" · ")}
                tags={outcome && <Tag tone={outcome.tone}>{outcome.label}</Tag>}
                onClick={() => onOpen(m)}
            />
        );
    };

    return (
        <FoldCard id="emails" title="Emails" icon={{ icon: MailIcon, area: "setup" }} foldedFact={count > 0 ? `${count} sent` : "Nothing sent yet"} defaultFolded={count === 0}>
            {count === 0 ? (
                <div className="flex items-center gap-3">
                    <IconSquare tone="gray" icon={MailIcon} />
                    <p className={cn(TYPE.tb, "m-0 text-gray-500")}>{isOneSetup(o) ? "Nothing sent yet. The Setup email goes first and asks for everything on one page." : "Nothing sent yet. The kick-off email goes first and asks the client for the form."}</p>
                </div>
            ) : (
                <>
                    <p className={cn(TYPE.tc, "m-0 mb-1 text-gray-500")}>{summary}</p>
                    {resultsRow}
                    {sent.slice(0, SHOWN).map(row)}
                    <ShowMore count={Math.max(0, sent.length - SHOWN)}>{sent.slice(SHOWN).map(row)}</ShowMore>
                </>
            )}
        </FoldCard>
    );
}
